import { useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export function useCountingInvoices() {
  return useQuery({
    queryKey: ["counting"],
    queryFn: () => fetchJson<any[]>("/api/counting"),
  });
}

export function useCountingInvoice(id: string | null) {
  return useQuery({
    queryKey: ["counting", id],
    queryFn: () => fetchJson<any>(`/api/counting/${id}`),
    enabled: !!id,
  });
}

export function useCreateCountingInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; date: number }) =>
      fetchJson("/api/counting", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["counting"] }),
  });
}

export function useUpdateCountingInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; date: number }) =>
      fetchJson(`/api/counting/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["counting"] });
      qc.invalidateQueries({ queryKey: ["counting", vars.id] });
    },
  });
}

export function useDeleteCountingInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/counting/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["counting"] }),
  });
}

export function useSetCountingStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, expectedStatus }: { id: string; status: string; expectedStatus?: string }) =>
      fetchJson(`/api/counting/${id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, expectedStatus }),
      }),
    onSettled: (_, _error, vars) => {
      qc.invalidateQueries({ queryKey: ["counting"] });
      qc.invalidateQueries({ queryKey: ["counting", vars.id] });
    },
  });
}

export function useAddCountingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, ...body }: { invoiceId: string; vendor_code: string; name: string; quantity: number }) =>
      fetchJson(`/api/counting/${invoiceId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useUpdateCountingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, invoiceId, ...body }: { itemId: string; invoiceId: string; vendor_code: string; name: string; quantity: number }) =>
      fetchJson(`/api/counting/items/${itemId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useDeleteCountingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, invoiceId }: { itemId: string; invoiceId: string }) =>
      fetchJson(`/api/counting/items/${itemId}`, { method: "DELETE" }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useAddCountingVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, invoiceId, ...body }: { itemId: string; invoiceId: string; sku: string; expected_qty: number }) =>
      fetchJson(`/api/counting/items/${itemId}/variations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useBulkCountingVariations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, invoiceId, variations }: { itemId: string; invoiceId: string; variations: { sku: string; expected_qty: number }[] }) =>
      fetchJson(`/api/counting/items/${itemId}/variations/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ variations }),
      }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useUpdateCountingVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ varId, invoiceId, ...body }: { varId: string; invoiceId: string; sku: string; expected_qty: number }) =>
      fetchJson(`/api/counting/variations/${varId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useDeleteCountingVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ varId, invoiceId }: { varId: string; invoiceId: string }) =>
      fetchJson(`/api/counting/variations/${varId}`, { method: "DELETE" }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useIncrementCountingVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ varId, delta, requestId }: { varId: string; delta: number; invoiceId: string; requestId: string }) =>
      fetchJson<any>(`/api/counting/variations/${varId}/increment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": requestId },
        body: JSON.stringify({ delta }),
      }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useSetCountingVariationCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ varId, value }: { varId: string; value: number; invoiceId: string }) =>
      fetchJson<any>(`/api/counting/variations/${varId}/count`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useResetCountingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, invoiceId }: { itemId: string; invoiceId: string }) =>
      fetchJson(`/api/counting/items/${itemId}/reset`, { method: "POST" }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export function useResetAllCounting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId }: { invoiceId: string }) =>
      fetchJson(`/api/counting/${invoiceId}/reset`, { method: "POST" }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["counting", vars.invoiceId] }),
  });
}

export type OcrProgressState = {
  pagesDone: number;
  pagesTotal: number;
  itemsFound: number;
  headerName: string | null;
  failedRanges: { fromPage: number; toPage: number }[];
};

/**
 * Linear extrapolation from observed throughput. Equivalent to the spec's
 * avgBatchDuration × ceil(remaining / CONCURRENCY) for a steady worker pool,
 * without needing per-batch timings.
 */
export function estimateRemainingMs(
  elapsedMs: number,
  pagesDone: number,
  pagesTotal: number,
): number | null {
  if (pagesDone <= 0 || pagesDone >= pagesTotal) return null;
  return (elapsedMs * (pagesTotal - pagesDone)) / pagesDone;
}

/** True za grešku koja je posljedica korisnikovog prekida, ne kvara. */
export function isOcrAbortError(e: unknown): boolean {
  // Duck-typed: prekinuti fetch baca DOMException, koja u pretraživačima nije
  // instanceof Error, pa se oslanjamo samo na `name`.
  return typeof e === "object" && e !== null && (e as { name?: string }).name === "AbortError";
}

export function useProcessCountingOcr(onProgress?: (s: OcrProgressState) => void) {
  // Svjež kontroler po pokretanju — prekinuti upload ne smije onemogućiti sljedeći.
  const controllerRef = useRef<AbortController | null>(null);
  const abort = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const controller = new AbortController();
      controllerRef.current = controller;
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/counting/ocr", {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const body = await res.json().catch(() => ({ error: "Greška pri obradi fajla" }));
        throw new Error(body.error || "Greška pri obradi fajla");
      }

      const state: OcrProgressState = {
        pagesDone: 0,
        pagesTotal: 0,
        itemsFound: 0,
        headerName: null,
        failedRanges: [],
      };

      // Server streama NDJSON: heartbeat linije dok OCR traje, pa zadnja linija
      // {type:"result",data} ili {type:"error"}. Čitamo dok ne dođe finalna linija.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let result: any = null;

      const handleLine = (line: string) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        const event = JSON.parse(trimmed);
        switch (event.type) {
          case "heartbeat":
            return;
          case "header":
            state.headerName = event.data?.name ?? null;
            onProgress?.({ ...state });
            return;
          case "progress":
            state.pagesDone = event.pagesDone;
            state.pagesTotal = event.pagesTotal;
            state.itemsFound = event.itemsFound;
            onProgress?.({ ...state });
            return;
          case "failed":
            state.failedRanges = [...state.failedRanges, { fromPage: event.fromPage, toPage: event.toPage }];
            onProgress?.({ ...state });
            return;
          case "recovered":
            state.failedRanges = state.failedRanges.filter(
              (r) => !(r.fromPage === event.fromPage && r.toPage === event.toPage),
            );
            onProgress?.({ ...state });
            return;
          case "error":
            throw new Error(event.error || "Greška pri obradi fajla");
          case "result":
            result = event.data;
            return;
        }
      };

      // Prekid (korisnik zatvorio dijalog) natjera reader.read() da odbaci
      // AbortError — propušta se netaknut da ga pozivalac prepozna i ne
      // prikaže kao grešku.
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let nl: number;
          while ((nl = buffer.indexOf("\n")) !== -1) {
            const line = buffer.slice(0, nl);
            buffer = buffer.slice(nl + 1);
            handleLine(line);
          }
        }
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
      if (buffer.trim()) handleLine(buffer);

      if (!result) throw new Error("Greška pri obradi fajla");
      return result;
    },
  });

  return Object.assign(mutation, { abort });
}

export function useCreateFromOcr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; date: string; items: any[]; filePath?: string }) =>
      fetchJson("/api/counting/from-ocr", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["counting"] }),
  });
}
